import React from 'react';
import { FiArrowRight } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { useDarkMode } from '../context/DarkModeContext';

const Hero = () => {
  const { isDark } = useDarkMode();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  const logoVariants = {
    hidden: { opacity: 0, scale: 0.8, y: -20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 1,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section
      id="home"
      className={`relative w-full min-h-screen flex items-center justify-center overflow-hidden pt-20 transition-all duration-500 ${
        isDark
          ? 'bg-gradient-to-br from-dark-primary via-black to-dark-secondary'
          : 'bg-gradient-to-br from-white via-gray-50 to-gray-100'
      }`}
    >
      {/* Animated Background Elements - Enhanced */}
      <motion.div
        className={`absolute top-10 left-10 w-96 h-96 rounded-full blur-3xl ${
          isDark ? 'bg-primary-gold/15' : 'bg-primary-gold/10'
        }`}
        animate={{
          scale: [1, 1.15, 1],
          opacity: [0.5, 0.7, 0.5],
          x: [0, 30, 0],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      ></motion.div>

      <motion.div
        className={`absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl ${
          isDark ? 'bg-primary-gold/10' : 'bg-primary-gold/8'
        }`}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.4, 0.6, 0.4],
          x: [0, -30, 0],
        }}
        transition={{ duration: 9, repeat: Infinity, delay: 0.5, ease: 'easeInOut' }}
      ></motion.div>

      {/* Additional floating shapes for premium feel */}
      <motion.div
        className={`absolute top-1/3 right-1/4 w-64 h-64 rounded-full blur-2xl ${
          isDark ? 'bg-primary-gold/8' : 'bg-primary-gold/5'
        }`}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.2, 0.4, 0.2],
          y: [0, 40, 0],
        }}
        transition={{ duration: 10, repeat: Infinity, delay: 1, ease: 'easeInOut' }}
      ></motion.div>

      <motion.div
        className={`absolute bottom-1/4 left-1/3 w-80 h-80 rounded-full blur-3xl ${
          isDark ? 'bg-primary-gold/5' : 'bg-primary-gold/4'
        }`}
        animate={{
          scale: [1, 1.15, 1],
          opacity: [0.3, 0.5, 0.3],
          y: [0, -30, 0],
        }}
        transition={{ duration: 11, repeat: Infinity, delay: 1.5, ease: 'easeInOut' }}
      ></motion.div>

      {/* Top accent gradient */}
      <motion.div
        className={`absolute top-0 left-1/2 -translate-x-1/2 w-full h-40 pointer-events-none ${
          isDark
            ? 'bg-gradient-to-b from-primary-gold/5 to-transparent'
            : 'bg-gradient-to-b from-primary-gold/8 to-transparent'
        }`}
        animate={{ opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 4, repeat: Infinity }}
      ></motion.div>

      {/* Content */}
      <motion.div
        className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="space-y-8">
          {/* Logo Animation */}
          <motion.div
            className="flex justify-center mb-6"
            variants={logoVariants}
            animate={{ y: [0, -15, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <motion.div
              className="relative"
              animate={{
                rotateY: [0, 360],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: 'linear',
              }}
              style={{ perspective: '1000px' }}
            >
              <div
                className={`w-24 h-24 sm:w-32 sm:h-32 rounded-full border-3 border-primary-gold flex items-center justify-center relative ${
                  isDark ? 'shadow-gold-glow' : 'shadow-lg'
                }`}
              >
                <span className="font-montserrat font-bold text-2xl sm:text-3xl text-primary-gold">
                  MS
                </span>
              </div>
              {/* Rotating ring */}
              <motion.div
                className="absolute inset-0 rounded-full border-2 border-transparent border-t-primary-gold border-r-primary-gold"
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              ></motion.div>
            </motion.div>
          </motion.div>

          {/* Tagline Above Headline */}
          <motion.div
            className="space-y-2"
            variants={itemVariants}
          >
            <p className={`font-montserrat font-semibold uppercase tracking-widest text-sm ${
              isDark ? 'text-primary-gold' : 'text-primary-gold'
            }`}>
              Premium Social Media Management
            </p>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            className={`font-montserrat font-bold text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-tight transition-colors ${
              isDark ? 'text-white' : 'text-black'
            }`}
            variants={itemVariants}
          >
            Content{' '}
            <motion.span
              className="text-primary-gold inline-block"
              animate={{
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
              }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              .
            </motion.span>{' '}
            Growth{' '}
            <motion.span
              className="text-primary-gold inline-block"
              animate={{
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
              }}
              transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
            >
              .
            </motion.span>{' '}
            <span>Influence</span>
          </motion.h1>

          {/* Enhanced Subheading */}
          <motion.p
            className={`font-poppins text-lg sm:text-xl lg:text-2xl max-w-4xl mx-auto leading-relaxed transition-colors ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}
            variants={itemVariants}
          >
            We help brands dominate social media through strategic content, influencer partnerships, and data-driven growth systems. Media Society transforms your online presence into authority, engagement, and revenue.
          </motion.p>

          {/* Supporting Text */}
          <motion.p
            className={`font-poppins text-base sm:text-lg max-w-2xl mx-auto transition-colors ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}
            variants={itemVariants}
          >
            Premium Social Media Management & Influencer Marketing Agency based in Lahore, Pakistan.
          </motion.p>

          {/* Enhanced CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center mt-12"
            variants={itemVariants}
          >
            {/* Primary Button */}
            <motion.button
              onClick={() => scrollToSection('contact')}
              className={`group px-8 sm:px-10 py-4 rounded-lg font-montserrat font-bold text-lg transition-all duration-300 flex items-center justify-center gap-2 ${
                isDark
                  ? 'bg-primary-gold text-black hover:shadow-gold-glow-lg'
                  : 'bg-primary-gold text-black hover:shadow-lg'
              }`}
              whileHover={{
                y: -6,
                scale: 1.06,
              }}
              whileTap={{ scale: 0.94 }}
            >
              Get Free Audit
              <motion.div
                animate={{ x: [0, 6, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <FiArrowRight size={20} />
              </motion.div>
            </motion.button>

            {/* Secondary Button */}
            <motion.button
              onClick={() => scrollToSection('portfolio')}
              className={`px-8 sm:px-10 py-4 border-2 border-primary-gold font-montserrat font-bold text-lg rounded-lg transition-all duration-300 ${
                isDark
                  ? 'text-primary-gold hover:bg-primary-gold/10'
                  : 'text-primary-gold hover:bg-primary-gold/5'
              }`}
              whileHover={{
                y: -6,
                scale: 1.06,
              }}
              whileTap={{ scale: 0.94 }}
            >
              View Our Work
            </motion.button>
          </motion.div>

          {/* Trust/Stats Section - Enhanced */}
          <motion.div
            className={`flex flex-col sm:flex-row justify-center gap-8 mt-16 pt-8 transition-colors ${
              isDark ? 'border-primary-gold/20' : 'border-primary-gold/30'
            } border-t`}
            variants={itemVariants}
          >
            {[
              { number: '500+', label: 'Campaigns Run' },
              { number: '1000+', label: 'Brands Managed' },
              { number: '300%', label: 'Avg. Growth Rate' },
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                className="text-center"
                whileHover={{
                  scale: 1.12,
                  y: -5,
                }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <motion.p
                  className="text-4xl sm:text-5xl font-montserrat font-bold text-primary-gold"
                  animate={{
                    scale: [1, 1.05, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: idx * 0.2,
                  }}
                >
                  {stat.number}
                </motion.p>
                <p className={`font-poppins mt-2 text-sm sm:text-base transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      {/* Enhanced Scroll Indicator */}
      <motion.div
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20"
        animate={{ y: [0, 12, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        <motion.div
          className={`w-8 h-12 border-2 border-primary-gold rounded-full flex items-start justify-center p-2 ${
            isDark ? 'shadow-gold-glow' : 'shadow-lg'
          }`}
          animate={{
            boxShadow: [
              isDark
                ? '0 0 20px rgba(255, 215, 0, 0.3)'
                : '0 0 10px rgba(255, 215, 0, 0.2)',
              isDark
                ? '0 0 40px rgba(255, 215, 0, 0.5)'
                : '0 0 20px rgba(255, 215, 0, 0.4)',
              isDark
                ? '0 0 20px rgba(255, 215, 0, 0.3)'
                : '0 0 10px rgba(255, 215, 0, 0.2)',
            ],
          }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <motion.div
            className="w-1 h-2 bg-primary-gold rounded-full"
            animate={{ opacity: [1, 0.5, 1], y: [0, 6, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          ></motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
